import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from './common'

/**
 * «Trova doppioni» dalla lista dei Configuration Item (solo amministratori):
 * l'anteprima dei CI che sono lo stesso oggetto, con la prova accanto a ogni
 * gruppo, e l'unione dei gruppi scelti. È lo stesso lavoro di
 * `scripts/dedupe_cis.py`, senza terminale. Niente si modifica finché non si
 * preme «Unisci»: «stesso seriale» e «stesso indirizzo» sono certezze diverse,
 * e l'unico momento per accorgersene è prima di unire.
 */
export function DoppioniCi({ onChiudi, onUniti }: { onChiudi: () => void; onUniti: () => void }) {
  const anteprima = useAsync(() => api.ciDuplicates(), [])
  const gruppi = anteprima.data?.groups ?? []
  const [esclusi, setEsclusi] = useState<Set<string>>(new Set())
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const scelti = gruppi.filter((g) => !esclusi.has(g.key))
  const coppieHa = anteprima.data?.ha_pairs ?? []

  async function registraHa() {
    setErrore(null)
    setLavoro(true)
    try {
      const r = await api.registerHaPairs()
      setEsito(t('Registrate {n} coppie in alta affidabilità.', { n: r.registered }))
      onUniti()
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }

  function alterna(chiave: string) {
    setEsclusi((prima) => {
      const dopo = new Set(prima)
      if (dopo.has(chiave)) dopo.delete(chiave)
      else dopo.add(chiave)
      return dopo
    })
  }

  async function unisci() {
    const n = scelti.reduce((somma, g) => somma + g.victims.length, 0)
    if (!window.confirm(t('Unire {g} gruppi? {n} CI verranno assorbiti nel CI che resta di ciascun gruppo; i loro identificativi continueranno a portare lì.', { g: scelti.length, n }))) return
    setErrore(null)
    setLavoro(true)
    try {
      const r = await api.mergeCiDuplicates(scelti.map((g) => g.key))
      setEsito(t('Uniti {g} gruppi, {n} CI assorbiti.', { g: r.merged_groups, n: r.absorbed }))
      setEsclusi(new Set())
      await anteprima.reload()
      onUniti()
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <Card
      title={t('Doppioni')}
      actions={
        <button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>
      }
    >
      <p className="field__hint">
        {t('CI che risultano lo stesso oggetto: per ogni gruppo, quello che resta e quelli che verrebbero assorbiti, con la prova (le chiavi in comune). Niente cambia finché non premi «Unisci». Togli la spunta ai gruppi che non ti convincono.')}
      </p>
      <ErrorBanner error={anteprima.error ?? errore} />
      {esito && <div className="banner banner--info">{esito}</div>}
      {coppieHa.length > 0 && (
        <div className="banner banner--info">
          <strong>{t('Coppie in alta affidabilità ({n})', { n: coppieHa.length })}</strong>
          <p style={{ margin: '4px 0' }}>
            {t('Condividono i MAC virtuali del cluster ma hanno seriali diversi: sono due apparati in HA, non doppioni, e non vengono uniti.')}
          </p>
          <ul style={{ margin: '4px 0 8px 18px' }}>
            {coppieHa.map((c) => (
              <li key={`${c.a.id}-${c.b.id}`}>
                <Link to={`/cis/${c.a.id}`}>{c.a.name}</Link>{' ⇄ '}<Link to={`/cis/${c.b.id}`}>{c.b.name}</Link>
                <span className="muted mono"> · {c.shared_macs.slice(0, 3).join(', ')}{c.shared_macs.length > 3 ? '…' : ''}</span>
              </li>
            ))}
          </ul>
          <button type="button" className="secondary" onClick={() => void registraHa()} disabled={lavoro}>
            {t('Registra come coppie HA')}
          </button>
        </div>
      )}
      {anteprima.loading && !anteprima.data ? (
        <Spinner label={t('Cerco i doppioni…')} />
      ) : gruppi.length === 0 ? (
        <EmptyState title={t('Nessun doppione')} hint={t('Ogni oggetto compare una volta sola.')} />
      ) : (
        <>
          <div className="doppioni">
            {gruppi.map((g) => (
              <label key={g.key} className={`doppioni__gruppo${esclusi.has(g.key) ? ' doppioni__gruppo--escluso' : ''}`}>
                <input type="checkbox" checked={!esclusi.has(g.key)} onChange={() => alterna(g.key)} />
                <div>
                  <div>
                    <span className="muted">{t('Resta')}</span>{' '}
                    <Link to={`/cis/${g.survivor.id}`} onClick={(e) => e.stopPropagation()}>
                      <strong>{g.survivor.name}</strong>
                    </Link>{' '}
                    <span className="muted mono">{g.survivor.primary_key}</span>
                  </div>
                  {g.victims.map((v) => (
                    <div key={v.id}>
                      <span className="muted">{t('Assorbito')}</span>{' '}
                      <Link to={`/cis/${v.id}`} onClick={(e) => e.stopPropagation()}>{v.name}</Link>{' '}
                      <span className="muted mono">{v.primary_key}</span>
                      {v.last_seen && <span className="muted"> · {t('visto {quando}', { quando: formatDateTime(v.last_seen) })}</span>}
                    </div>
                  ))}
                  <div className="field__hint" style={{ margin: 0 }}>
                    {g.kind === 'address' ? t('Host visto dallo sweep su un indirizzo aggiuntivo già in CMDB.') : t('Prova')}
                    {': '}<span className="mono">{g.evidence.join(', ') || '—'}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div className="rowactions" style={{ textAlign: 'left', marginTop: 12 }}>
            <button type="button" onClick={() => void unisci()} disabled={lavoro || scelti.length === 0}>
              {lavoro ? t('Unisco…') : t('Unisci {n} gruppi', { n: scelti.length })}
            </button>
          </div>
        </>
      )}
    </Card>
  )
}
